/**
 * Вкладка «Журнал» страницы «Расширение» (`?tab=events`): что сотрудник делал в окне расширения —
 * какой диалог открыл, что нажал в панели, что ответил prokat, отправил ли сообщение, — и что по
 * этим нажатиям сделал сервер (записи `user_action`). Вкладка площадки шлёт события раз в минуту.
 * Спека `docs/specs/2026-10-05-ext-telemetry-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { loadExtEvents, type ExtEvent, type ExtEventsFilter, type ExtUser } from "@/lib/extension";

const ПЛОЩАДКА: Record<string, string> = { telegram: "Telegram", max: "MAX", whatsapp: "WhatsApp" };
const VIEW: Record<string, string> = {
  "check-login": "проверяет вход",
  login: "просит войти",
  "prokat-error": "prokat не ответил",
  "platform-loading": "площадка загружается",
  "no-dialog": "диалог не открыт",
  "not-person": "не личный диалог",
  dialog: "диалог клиента",
};
const СЕРВЕР: Record<string, string> = {
  stage: "стадия сделки",
  wait: "дата записи / обработки",
  rent: "даты проката",
  location: "город сделки",
  event: "мероприятие",
  zapis_link: "ссылка на запись",
  deal_create: "создание сделки",
  open_bitrix: "переход в Bitrix",
  open_crm: "переход в CRM",
  dress_search: "поиск похожих платьев",
  dress_pick: "подборка платьев",
  answered: "отметка «Ответили»",
};
const input =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-transparent px-2 text-[13px] hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent";
const fmtPhone = (p: string) => (p.length === 11 && p.startsWith("7") ? `+7 ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9)}` : `+${p}`);
const s = (v: unknown) => (v == null ? "" : String(v));
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Событие словами: что человек сделал или что ему ответили. */
function words(e: ExtEvent): { text: string; tone: "" | "ok" | "err" | "muted" } {
  const d = e.data;
  if (e.src === "server") {
    const что = СЕРВЕР[e.code] ?? e.code;
    return e.error ? { text: `Сервер: ${что} — не прошло: ${e.error}`, tone: "err" } : { text: `Сервер: ${что} — выполнено`, tone: "ok" };
  }
  switch (e.code) {
    case "state":
      return { text: `На связи: ${VIEW[s(d["view"])] ?? (s(d["view"]) || "панель")}${d["visible"] === false ? ", вкладка в фоне" : ""}`, tone: "muted" };
    case "peer":
      return { text: e.peer_id ? "Открыл диалог" : "Диалог закрыт", tone: "" };
    case "click":
      if (d["src"] === "panel") return { text: `Нажал в панели: «${s(d["what"])}»`, tone: "" };
      if (d["where"] === "frame") return { text: `Нажал в шапке панели: «${s(d["what"])}»`, tone: "" };
      return { text: `Нажал на странице: ${s(d["what"])}`, tone: "muted" };
    case "key":
      return { text: `Клавиша ${s(d["key"])}${d["src"] === "panel" ? " в панели" : ""}`, tone: "muted" };
    case "send":
      return { text: "Отправил сообщение клиенту", tone: "ok" };
    case "api": {
      const код = Number(d["status"]);
      const плохо = !(код >= 200 && код < 300);
      return { text: `Запрос ${s(d["method"]) || "GET"} ${s(d["path"])} → ${код || "нет ответа"} · ${s(d["ms"])} мс`, tone: плохо ? "err" : "muted" };
    }
    case "view":
      return { text: `Панель показала: ${VIEW[s(d["view"])] ?? s(d["view"])}`, tone: "muted" };
    case "lookup":
      return { text: `Панель определила: номер ${s(d["phone"]) || "—"}${Number(d["deal"]) ? `, сделка ${s(d["deal"])}` : Number(d["lead"]) ? `, лид ${s(d["lead"])}` : ", сделки нет"}`, tone: "muted" };
    case "insert":
      return d["error"] ? { text: `Текст не встал в поле: ${s(d["error"])}`, tone: "err" } : { text: "Вставил текст в поле сообщения", tone: "" };
    case "visible":
      return { text: d["state"] === "visible" ? "Вернулся на вкладку" : "Ушёл со вкладки", tone: "muted" };
    case "mismatch":
      return { text: "Открытый диалог не сошёлся с данными площадки", tone: "err" };
    case "error":
      return { text: `Ошибка расширения: ${s(d["message"])}`, tone: "err" };
    case "start":
      return { text: `Вкладка загружена · версия ${s(d["version"])}`, tone: "muted" };
    default:
      return { text: e.code, tone: "muted" };
  }
}
const TONE = { "": "text-fg", ok: "text-ok", err: "text-err", muted: "text-fg-muted" };

function Row({ e, all }: { e: ExtEvent; all: boolean }) {
  const w = words(e);
  const [open, setOpen] = useState(false);
  const время = e.at.slice(11, 19);
  const счёт = e.account.phone ? fmtPhone(e.account.phone) : e.account.id;
  return (
    <>
      <tr className="cursor-pointer border-t border-border align-top hover:bg-bg-hover" onClick={() => setOpen(!open)}>
        <td className="whitespace-nowrap py-1 pr-3 font-mono tabular-nums text-fg-faint">{время}</td>
        {all && <td className="whitespace-nowrap py-1 pr-3">{e.user}</td>}
        <td className="whitespace-nowrap py-1 pr-3 text-fg-muted">
          {e.src === "server" ? "сервер" : `${ПЛОЩАДКА[e.channel] ?? e.channel}${счёт ? ` · ${e.account.name ? `«${e.account.name}» ` : ""}${счёт}` : ""}`}
        </td>
        <td className="whitespace-nowrap py-1 pr-3">
          {e.phone ? fmtPhone(e.phone) : e.peer_name}
          {e.deal_id ? (
            <>
              {e.phone || e.peer_name ? " · " : ""}
              <Link to={`/deals/${e.deal_id}`} className="font-mono text-accent-text hover:underline" onClick={(ev) => ev.stopPropagation()}>
                {e.deal_id}
              </Link>
            </>
          ) : null}
        </td>
        <td className={`py-1 ${TONE[w.tone]}`}>
          {w.text}
          {e.count > 1 ? <span className="text-fg-faint"> ×{e.count}</span> : null}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={all ? 5 : 4} className="pb-2">
            <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-[var(--radius-ui)] bg-bg-subtle p-2 font-mono text-[12px] text-fg-muted">
              {JSON.stringify({ code: e.code, version: e.version, peer: e.peer_id, name: e.peer_name, chat: e.chat_id, ...e.data }, null, 1)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

export function Journal({ users }: { users: ExtUser[] }) {
  const [f, setF] = useState<ExtEventsFilter>({ user: "", deal: "", phone: "", day: today(), state: false });
  const q = useQuery({ queryKey: ["ext-events", f], queryFn: () => loadExtEvents(f), refetchInterval: 30_000, placeholderData: (prev) => prev });
  const all = q.data?.all ?? false;
  const set = (patch: Partial<ExtEventsFilter>) => setF({ ...f, ...patch });
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[13.5px]" data-annot="что в журнале">
        <div className="font-semibold">Что сотрудник делал в окне расширения</div>
        <div className="mt-1 text-fg-muted">
          Вкладка WhatsApp, Telegram или MAX раз в минуту присылает свои события: какой диалог открыт, что нажато в панели,
          что ответил prokat, отправлено ли сообщение. Строки «сервер» — то, что по этим нажатиям действительно записано
          (и ошибка, если не прошло). Текст переписки сюда не попадает. Нажмите на строку — подробности.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-annot="фильтры журнала">
        {all && (
          <select className={input} value={f.user} onChange={(e) => set({ user: e.target.value })} aria-label="Сотрудник">
            <option value="">все сотрудники</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        )}
        <input type="date" className={input} value={f.day} onChange={(e) => set({ day: e.target.value })} aria-label="День" />
        <input className={`${input} w-[120px]`} placeholder="сделка" inputMode="numeric" value={f.deal} onChange={(e) => set({ deal: e.target.value.replace(/\D/g, "") })} />
        <input className={`${input} w-[170px]`} placeholder="номер клиента" value={f.phone} onChange={(e) => set({ phone: e.target.value })} />
        <label className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
          <input type="checkbox" checked={f.state} onChange={(e) => set({ state: e.target.checked })} />
          отметки «на связи» раз в минуту
        </label>
      </div>
      {q.isPending && <div className="text-[13px] text-fg-muted">Загрузка…</div>}
      {q.isError && <div className="text-[13px] text-err">{q.error.message}</div>}
      {q.data && (
        <>
          <div className="text-[12.5px] text-fg-faint">
            {all ? "Действия всех сотрудников" : "Ваши действия"}, новые сверху · {q.data.events.length} строк
          </div>
          {q.data.events.length === 0 ? (
            <div className="text-[13px] text-fg-muted">За этот день событий нет.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[12.5px]" data-annot="журнал действий">
                <thead className="text-[11px] uppercase tracking-[0.06em] text-fg-faint">
                  <tr>
                    <th className="pb-1 pr-3 font-medium">Время</th>
                    {all && <th className="pb-1 pr-3 font-medium">Сотрудник</th>}
                    <th className="pb-1 pr-3 font-medium">Где</th>
                    <th className="pb-1 pr-3 font-medium">Клиент · сделка</th>
                    <th className="pb-1 font-medium">Что произошло</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.events.map((e) => (
                    <Row key={`${e.src}${e.id}`} e={e} all={all} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
